import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import qs from 'qs';
import { isObject } from '../utils/helpers';
import logger from './logger';
import { userStore } from 'src/mobx/userStore';

class Axios {
  private defaultHeaders = {
    'Content-Type': 'application/json',
    'x-channel': 'a', // a - admin panel
    // Accept: 'application/json',
    // 'x-device-id': 'taker_staff',
    // 'Content-Type': 'application/json'
    // 'Access-Control-Allow-Origin': '*',
    'Accept-Language': 'en,ar',
  };

  private client = axios.create({
    timeout: 30000,
    headers: this.defaultHeaders,
    baseURL: '',
    transformRequest: this.transformRequest,
    paramsSerializer: this.paramsSerializer,
  });

  constructor() {
    this.configureRetry();
  }

  private errorHandler(options: any, error: AxiosError) {
    logger.log(error, options);

    if (error.response && error.response.status === 401) {
      // Do not call logout method if we trying to login
      if (options.url !== '/v3/auth/login') {
        userStore.logout();
      }
    }

    throw error;
  }

  private transformRequest(this: void, data = {}) {
    if (data instanceof FormData) return data;
    if (Array.isArray(data)) return JSON.stringify(data);
    else if (!isObject(data) || !Object.keys(data).length) return JSON.stringify({});
    data = Object.entries(data).reduce((acc, [key, value]) => {
      if (value === undefined) return acc;
      return { ...acc, [key]: value };
    }, {});
    return JSON.stringify(data);
  }

  private paramsSerializer(this: void, data = {}) {
    if (!isObject(data)) return String(data);
    data = Object.entries(data).reduce((acc, [key, value]) => {
      if (value === undefined || value === '') return acc;
      return { ...acc, [key]: value };
    }, {});
    return qs.stringify(data, { arrayFormat: 'brackets' });
  }

  public get headers(): { [key: string]: string } {
    return this.defaultHeaders;
  }

  public set headers(headers: AxiosRequestConfig['headers']) {
    this.defaultHeaders = {
      ...this.defaultHeaders,
      ...headers,
    };
    this.client.defaults.headers = {
      ...this.client.defaults.headers,
      ...headers,
    };
  }

  private endpoint(url: string) {
    if (url.includes('http') || url.includes('https')) return url;
    if (url[0] !== '/') url = `/${url}`;

    switch (process.env.NODE_ENV) {
      case 'test':
        break;
      case 'development':
        break;
      default:
        url = `${window.location.origin}/api${url}`;
        break;
    }
    return url;
  }

  async get(url: string, options?: AxiosRequestConfig, getWholeResponse?: boolean) {
    try {
      const endpoint = this.endpoint(url);
      const response = await this.client.get(endpoint, options);
      return getWholeResponse ? response : response.data;
    } catch (error: any) {
      this.errorHandler({ url, options }, error);
    }
  }

  async patch(url: string, body: any = {}, options?: AxiosRequestConfig) {
    try {
      const endpoint = this.endpoint(url);
      const response = await this.client.patch(endpoint, body, options);
      return response.data;
    } catch (error: any) {
      this.errorHandler({ url, body, options }, error);
    }
  }

  async post(url: string, body: any = {}, options?: AxiosRequestConfig) {
    try {
      const endpoint = this.endpoint(url);
      const response = await this.client.post(endpoint, body, options);
      return response.data;
    } catch (error: any) {
      this.errorHandler({ url, body, options }, error);
    }
  }

  async put(url: string, body: any = {}, options?: AxiosRequestConfig) {
    try {
      const endpoint = this.endpoint(url);
      const response = await this.client.put(endpoint, body, options);
      return response.data;
    } catch (error: any) {
      this.errorHandler({ url, body, options }, error);
    }
  }

  async delete(url: string, options?: AxiosRequestConfig) {
    try {
      const endpoint = this.endpoint(url);
      const response = await this.client.delete(endpoint, options);
      return response.data;
    } catch (error: any) {
      this.errorHandler({ url, options }, error);
    }
  }

  private configureRetry() {
    axiosRetry(this.client, {
      retries: 3,
      retryDelay: (retryCount) => {
        console.log(`retry attempt: ${retryCount}`);
        return retryCount * 2000;
      },
      retryCondition: (error) => {
        return error.response !== undefined && ![400, 401, 403, 404, 422].includes(error.response.status);
      },
      shouldResetTimeout: true,
    });
  }
}

const client = new Axios();
export default client;
